import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateAllocation } from '@/lib/api/allocations'
import { DEMO_CONFIG, DEMO_ORIGINAL_POSITIONS } from '../data/demoConfig'
import type { TowStep } from '../data/towingPlan'

export type DemoState =
  | 'idle'
  | 'syncing'
  | 'arrival_modal'
  | 'placing_hero'
  | 'planning'
  | 'result'
  | 'animating'
  | 'resetting'

interface DemoModeState {
  state: DemoState
  activeTowStep: TowStep | null
  completedStepIds: string[]
}

export function useDemoMode() {
  const queryClient = useQueryClient()
  const [demo, setDemo] = useState<DemoModeState>({
    state: 'idle',
    activeTowStep: null,
    completedStepIds: [],
  })

  const triggerSync = useCallback(() => {
    setDemo((d) => ({ ...d, state: 'syncing' }))
    // Fake 3.5-second "sync" then show arrival modal
    setTimeout(() => {
      setDemo((d) => ({ ...d, state: 'arrival_modal' }))
    }, 3500)
  }, [])

  const dismissArrival = useCallback(() => {
    setDemo((d) => ({ ...d, state: 'idle' }))
  }, [])

  // "Place Manually" — activate existing placement mode flow and dismiss the modal
  // The caller is responsible for triggering the wizard placement mode
  const startManualPlacement = useCallback(() => {
    setDemo((d) => ({ ...d, state: 'idle' }))
  }, [])

  // "AutoFit" — make the hero aircraft visible (presence=present) then start planning
  const startAutoFit = useCallback(async () => {
    setDemo((d) => ({ ...d, state: 'placing_hero' }))
    try {
      // Make the hero visible on the map: flip presence to present
      await updateAllocation(DEMO_CONFIG.heroAllocationId, {
        presence: 'present',
        status: 'active',
      })
      queryClient.invalidateQueries({ queryKey: ['allocations'] })
    } catch {
      // ignore — demo continues even if DB update fails
    }
    setDemo((d) => ({ ...d, state: 'planning' }))
  }, [queryClient])

  const finishPlanning = useCallback(() => {
    setDemo((d) => ({ ...d, state: 'result' }))
  }, [])

  const selectTowStep = useCallback((step: TowStep) => {
    setDemo((d) => ({ ...d, state: 'animating', activeTowStep: step }))
  }, [])

  const completeStep = useCallback((stepId: string) => {
    setDemo((d) => ({
      ...d,
      state: 'result',
      activeTowStep: null,
      completedStepIds: [...d.completedStepIds, stepId],
    }))
  }, [])

  const resetDemo = useCallback(async () => {
    setDemo((d) => ({ ...d, state: 'resetting', activeTowStep: null, completedStepIds: [] }))
    try {
      // Reset all 9 demo allocations to their original positions
      const allIds = [...DEMO_CONFIG.fleetAllocationIds, DEMO_CONFIG.heroAllocationId]
      await Promise.all(
        allIds.map((id) => {
          const orig = DEMO_ORIGINAL_POSITIONS[id]
          if (!orig) return Promise.resolve()
          return updateAllocation(id, {
            position_x: orig.position[0],
            position_y: orig.position[1],
            position_z: orig.position[2],
            rotation: orig.rotation,
            status: orig.status as 'active' | 'reserved' | 'completed' | 'cancelled',
            presence: orig.presence as 'expected' | 'present' | 'away' | 'departed',
            position_type: orig.positionType as 'hangar' | 'tiedown' | 'ramp' | 'unknown',
          })
        })
      )
      queryClient.invalidateQueries({ queryKey: ['allocations'] })
    } catch {
      // ignore
    }
    setDemo((d) => ({ ...d, state: 'idle' }))
  }, [queryClient])

  return {
    demoState: demo.state,
    activeTowStep: demo.activeTowStep,
    completedStepIds: demo.completedStepIds,
    triggerSync,
    dismissArrival,
    startManualPlacement,
    startAutoFit,
    finishPlanning,
    selectTowStep,
    completeStep,
    resetDemo,
    isActive: demo.state !== 'idle' && demo.state !== 'resetting',
  }
}
